import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import {
  Button,
  ChevronDownIcon,
  DateInput,
  DocumentTextIcon,
  GeneralList,
  GeneralListSortableTh,
  PrinterIcon,
  RefreshIcon,
  SearchIcon,
  Select,
  UserIcon,
  useGeneralListSort,
} from '../../components/ui';
import { formatDate as formatDateGlobal } from '../../config/appConfig';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { getListStateStorageKey } from '../../lib/listStatePersistence';
import { cn } from '../../lib/utils';
import { fetcher } from '../../services/fetcher';
import { CrewHelpHint } from './CrewReportSearchHeader';
import { CrewReportViewAction } from './reportShared';
import {
  CREW_LIST_PRINT_KEYS,
  CREW_LIST_PRINT_META,
  crewListPrintPath,
  type ImoCrewListPrintDetails,
} from './reports/crewListPrintMeta';
import { ImoCrewListDetailsModal } from './reports/ImoCrewListDetailsModal';
import type { CrewVesselListResponse, CrewVesselListRow } from './types';

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

type AppliedFilters = { vesselId: string; periodFrom: string; periodTo: string };

const LIST_ROUTE = '/crew/list';
const STORAGE_KEY = getListStateStorageKey(LIST_ROUTE);

function buildListPath(applied: AppliedFilters): string {
  const qs = new URLSearchParams({
    vesselId: applied.vesselId,
    periodFrom: applied.periodFrom,
    periodTo: applied.periodTo,
  });
  return `${LIST_ROUTE}?${qs.toString()}`;
}

function loadPersistedFilters(): AppliedFilters | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as Partial<AppliedFilters>;
    if (!p.vesselId || !p.periodFrom || !p.periodTo) return null;
    return {
      vesselId: String(p.vesselId),
      periodFrom: String(p.periodFrom),
      periodTo: String(p.periodTo),
    };
  } catch {
    return null;
  }
}

function persistFilters(applied: AppliedFilters | null): void {
  try {
    if (!applied) {
      sessionStorage.removeItem(STORAGE_KEY);
      return;
    }
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(applied));
  } catch {
    // quota / private mode
  }
}

function filtersFromSearchParams(params: URLSearchParams): AppliedFilters | null {
  const vesselId = params.get('vesselId')?.trim() ?? '';
  const periodFrom = params.get('periodFrom')?.trim() ?? '';
  const periodTo = params.get('periodTo')?.trim() ?? '';
  if (!vesselId || !periodFrom || !periodTo) return null;
  return { vesselId, periodFrom, periodTo };
}

function formatWage(row: CrewVesselListRow): string {
  if (row.wage == null) return '';
  const amount = typeof row.wage === 'number' ? row.wage : Number(row.wage);
  if (!Number.isFinite(amount)) return String(row.wage);
  const formatted = amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return row.wageCurrency ? `${formatted} ${row.wageCurrency}` : formatted;
}

function sortValue(row: CrewVesselListRow, key: string): string | number {
  switch (key) {
    case 'lastName':
      return (row.lastName || '').toLowerCase();
    case 'firstName':
      return (row.firstName || '').toLowerCase();
    case 'nationality':
      return (row.nationality || '').toLowerCase();
    case 'age':
      return row.age ?? -1;
    case 'lastRank':
      return row.rankSortOrder ?? 9999;
    case 'wage':
      return row.wage != null ? Number(row.wage) : -1;
    case 'status':
      return (row.status || '').toLowerCase();
    case 'signOnDate':
      return row.signOnDate || '';
    case 'monthsOnboard':
      return row.monthsOnboard ?? -1;
    case 'intentionSignOffDate':
      return row.intentionSignOffDate || '';
    case 'signOffDate':
      return row.signOffDate || '';
    case 'signOffPort':
      return (row.signOffPort || '').toLowerCase();
    case 'signOffReason':
      return (row.signOffReason || '').toLowerCase();
    case 'earliest':
      return row.earliest || '';
    case 'coeDate':
      return row.coeDate || '';
    case 'latest':
      return row.latest || '';
    case 'extCoeDate':
      return row.extCoeDate || '';
    default:
      return row.transactionId;
  }
}

const CrewListPage: React.FC = () => {
  const { colors } = useTheme();
  const { user } = useAuth();
  const agencyMode = String(user?.typeLocation ?? '').toUpperCase() === 'MANNING_AGENT';
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [printOpen, setPrintOpen] = useState(false);
  const [imoDetailsOpen, setImoDetailsOpen] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!printOpen) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (printRef.current?.contains(t)) return;
      setPrintOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [printOpen]);

  const initialApplied = useMemo(() => {
    return filtersFromSearchParams(searchParams) ?? loadPersistedFilters();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- hydrate once on mount

  const [vesselId, setVesselId] = useState<string>(() => initialApplied?.vesselId ?? '');
  const [periodFrom, setPeriodFrom] = useState(() => initialApplied?.periodFrom ?? todayIso());
  const [periodTo, setPeriodTo] = useState(() => initialApplied?.periodTo ?? todayIso());
  const [applied, setApplied] = useState<AppliedFilters | null>(() => initialApplied);

  const listReturnPath = applied ? buildListPath(applied) : LIST_ROUTE;

  const { data: lookups } = useSWR<{ vessels: Array<{ id: number; vesselName: string }> }>(
    '/crew/lookups',
    fetcher,
    { revalidateOnFocus: false },
  );

  const vesselOptions = useMemo(
    () =>
      (lookups?.vessels ?? []).map((v) => ({
        value: String(v.id),
        label: v.vesselName,
      })),
    [lookups?.vessels],
  );

  const listUrl = applied
    ? `/crew/vessel-list?vesselId=${encodeURIComponent(applied.vesselId)}&periodFrom=${encodeURIComponent(applied.periodFrom)}&periodTo=${encodeURIComponent(applied.periodTo)}`
    : null;

  const { data, error, isLoading, mutate } = useSWR<CrewVesselListResponse>(listUrl, fetcher, {
    revalidateOnFocus: false,
  });

  useEffect(() => {
    persistFilters(applied);
    if (!applied) {
      if (searchParams.toString()) setSearchParams({}, { replace: true });
      return;
    }
    const next = new URLSearchParams({
      vesselId: applied.vesselId,
      periodFrom: applied.periodFrom,
      periodTo: applied.periodTo,
    });
    if (searchParams.toString() !== next.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [applied, searchParams, setSearchParams]);

  const handleSearch = useCallback(() => {
    if (!vesselId) return;
    setApplied({ vesselId, periodFrom, periodTo });
  }, [vesselId, periodFrom, periodTo]);

  const clearFilters = () => {
    setVesselId('');
    setPeriodFrom(todayIso());
    setPeriodTo(todayIso());
    setApplied(null);
  };

  const openCrew = useCallback(
    (crewId: number) => {
      navigate(agencyMode ? `/crew/${crewId}/file` : `/crew/${crewId}`, {
        state: { from: listReturnPath },
      });
    },
    [agencyMode, listReturnPath, navigate],
  );

  const rows = data?.rows ?? [];

  const { sortConfig, handleSort } = useGeneralListSort(
    { key: 'lastRank', direction: 'asc' },
    {
      defaultDirectionForKey: (key) =>
        key === 'age' || key === 'wage' || key === 'monthsOnboard' ? 'desc' : 'asc',
    },
  );

  const sortedRows = useMemo(() => {
    const next = [...rows];
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    next.sort((a, b) => {
      const av = sortValue(a, sortConfig.key);
      const bv = sortValue(b, sortConfig.key);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      if (sortConfig.key === 'lastRank') {
        const byLast = (a.lastName || '').localeCompare(b.lastName || '', undefined, { sensitivity: 'base' });
        if (byLast !== 0) return byLast * dir;
        const byFirst = (a.firstName || '').localeCompare(b.firstName || '', undefined, { sensitivity: 'base' });
        if (byFirst !== 0) return byFirst * dir;
      }
      return (a.transactionId - b.transactionId) * dir;
    });
    return next;
  }, [rows, sortConfig.key, sortConfig.direction]);

  const tableFooter = useMemo(() => {
    const ages = rows.map((r) => r.age).filter((v): v is number => v != null && Number.isFinite(v));
    const wages = rows
      .map((r) => (r.wage != null ? Number(r.wage) : NaN))
      .filter((v) => Number.isFinite(v));
    const months = rows
      .map((r) => r.monthsOnboard)
      .filter((v): v is number => v != null && Number.isFinite(v));
    const currencies = Array.from(
      new Set(
        rows
          .map((r) => r.wageCurrency?.trim().toUpperCase())
          .filter((c): c is string => Boolean(c)),
      ),
    );
    return {
      avgAge: ages.length
        ? ages.reduce((a, b) => a + b, 0) / ages.length
        : null,
      totalWage: wages.length ? wages.reduce((a, b) => a + b, 0) : null,
      wageCurrency: currencies.length === 1 ? currencies[0] : currencies.length > 1 ? currencies.join(' · ') : null,
      totalMonths: months.length ? months.reduce((a, b) => a + b, 0) : null,
    };
  }, [rows]);

  const hasFilters = Boolean(vesselId || applied);

  return (
    <div className="flex h-full min-h-0 flex-col gap-2">
      <GeneralList
        className="min-h-0 flex-1"
        headerStart={
          <CrewHelpHint
            hint={{
              title: 'Crew List',
              body: 'Shows crew assigned to a vessel during the selected period, including rank, wage, sign-on/off dates, COE, and months onboard. Choose a vessel and date range, then search.',
            }}
            className="self-center"
          />
        }
        sorting={{ sortConfig, onSort: handleSort }}
        filtersInline
        filters={
          <div className="flex flex-wrap items-center gap-2">
            <div className="w-[14rem] max-w-full">
              <Select
                name="vesselId"
                value={vesselId}
                onChange={(e) => setVesselId(String(e.target.value))}
                options={vesselOptions}
                placeholder="Vessel"
                showClearButton
                searchPlaceholder="Search vessels…"
                triggerClassName="!h-10 !min-h-10 !py-0 box-border"
              />
            </div>
            <div className="w-[10rem] max-w-full">
              <DateInput
                id="crew-list-from"
                name="periodFrom"
                value={periodFrom}
                onChange={(e) => setPeriodFrom(e.target.value)}
                placeholder="From"
                className="!h-10 !min-h-10 !py-0 box-border"
              />
            </div>
            <div className="w-[10rem] max-w-full">
              <DateInput
                id="crew-list-to"
                name="periodTo"
                value={periodTo}
                onChange={(e) => setPeriodTo(e.target.value)}
                placeholder="To"
                className="!h-10 !min-h-10 !py-0 box-border"
              />
            </div>
          </div>
        }
        hasFilters={hasFilters}
        onClearFilters={clearFilters}
        actions={
          <div className="flex h-10 items-center gap-2">
            <Button type="button" className="h-10" disabled={!vesselId || isLoading} onClick={handleSearch}>
              <SearchIcon className="h-4 w-4" />
              Search
            </Button>
            {applied ? (
              <Button
                type="button"
                variant="outline"
                className="h-10"
                disabled={isLoading}
                onClick={() => void mutate()}
                title="Refresh"
              >
                <RefreshIcon className="h-4 w-4" />
                Refresh
              </Button>
            ) : null}
            {applied ? (
              <div className="relative z-20" ref={printRef}>
                <Button
                  type="button"
                  variant="outline"
                  className="h-10"
                  aria-haspopup="menu"
                  aria-expanded={printOpen}
                  onClick={() => setPrintOpen((v) => !v)}
                >
                  <PrinterIcon className="h-4 w-4" />
                  Print
                  <ChevronDownIcon
                    className={cn('h-3.5 w-3.5 transition-transform', printOpen && 'rotate-180')}
                  />
                </Button>
                {printOpen ? (
                  <div
                    role="menu"
                    className={cn(
                      'absolute right-0 z-50 mt-1 min-w-[16rem] overflow-hidden rounded-md border py-1 shadow-lg',
                      colors.surface,
                      colors.border,
                    )}
                  >
                    {CREW_LIST_PRINT_KEYS.map((key) => {
                      const meta = CREW_LIST_PRINT_META[key];
                      if (key === 'imo') {
                        return (
                          <button
                            key={key}
                            type="button"
                            role="menuitem"
                            className={cn(
                              'flex w-full flex-col gap-0.5 px-3 py-2 text-left hover:bg-black/5 dark:hover:bg-white/10',
                              colors.text,
                            )}
                            onClick={() => {
                              setPrintOpen(false);
                              setImoDetailsOpen(true);
                            }}
                          >
                            <span className="flex items-center gap-2 text-sm font-medium">
                              <DocumentTextIcon className="h-4 w-4 shrink-0 opacity-70" />
                              {meta.title}
                            </span>
                            <span className={cn('pl-6 text-[11px] leading-snug', colors.textMuted)}>
                              {meta.subtitle}
                            </span>
                          </button>
                        );
                      }
                      return (
                        <Link
                          key={key}
                          role="menuitem"
                          to={crewListPrintPath(key, applied)}
                          className={cn(
                            'flex w-full flex-col gap-0.5 px-3 py-2 text-left hover:bg-black/5 dark:hover:bg-white/10',
                            colors.text,
                          )}
                          onClick={() => setPrintOpen(false)}
                        >
                          <span className="flex items-center gap-2 text-sm font-medium">
                            <DocumentTextIcon className="h-4 w-4 shrink-0 opacity-70" />
                            {meta.title}
                          </span>
                          <span className={cn('pl-6 text-[11px] leading-snug', colors.textMuted)}>
                            {meta.subtitle}
                          </span>
                        </Link>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        }
        isLoading={Boolean(applied) && isLoading}
        loadingLabel="Loading crew list…"
        isEmpty={!error && Boolean(applied) && sortedRows.length === 0}
        emptyState={{
          icon: <UserIcon className="h-12 w-12" />,
          title: 'No crew assignments',
          hint: 'No crew assignments for this vessel and period.',
          action: hasFilters ? (
            <button
              type="button"
              onClick={clearFilters}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-xl border px-4 py-2.5 text-sm font-medium',
                colors.border,
                colors.text,
              )}
            >
              Clear filters
            </button>
          ) : undefined,
        }}
        banner={
          error ? (
            <p className={cn('rounded-xl border px-3 py-2 text-sm', colors.error)}>
              Could not load crew list.
            </p>
          ) : null
        }
      >
        {applied && !error && sortedRows.length > 0 ? (
          <table className="w-full min-w-[1400px] text-sm [&_th]:h-11 [&_th]:align-middle [&_td]:h-12 [&_td]:align-middle">
            <thead>
              <tr className={cn('border-b text-left text-xs uppercase tracking-wide', colors.border, colors.textMuted)}>
                <th className={cn('sticky top-0 z-10 w-12 font-medium', colors.surface)} />
                <th className={cn('sticky top-0 z-10 w-12 whitespace-nowrap font-medium', colors.surface, colors.textMuted)}>
                  A/A
                </th>
                <GeneralListSortableTh sortKey="lastName" className="whitespace-nowrap">
                  Lastname
                </GeneralListSortableTh>
                <GeneralListSortableTh sortKey="firstName" className="whitespace-nowrap">
                  Firstname
                </GeneralListSortableTh>
                <GeneralListSortableTh sortKey="nationality" className="whitespace-nowrap">
                  Nationality
                </GeneralListSortableTh>
                <GeneralListSortableTh sortKey="age" className="whitespace-nowrap" align="right">
                  Age
                </GeneralListSortableTh>
                <GeneralListSortableTh sortKey="lastRank" className="whitespace-nowrap">
                  Last Rank
                </GeneralListSortableTh>
                {!agencyMode && (
                  <GeneralListSortableTh sortKey="wage" className="whitespace-nowrap" align="right">
                    Wage
                  </GeneralListSortableTh>
                )}
                <GeneralListSortableTh sortKey="status" className="whitespace-nowrap">
                  Status
                </GeneralListSortableTh>
                <th className={cn('sticky top-0 z-10 whitespace-nowrap font-medium', colors.surface, colors.textMuted)}>
                  Departure
                </th>
                <GeneralListSortableTh sortKey="signOnDate" className="whitespace-nowrap">
                  S/On Date
                </GeneralListSortableTh>
                <GeneralListSortableTh sortKey="monthsOnboard" className="whitespace-nowrap" align="right">
                  M.ONB
                </GeneralListSortableTh>
                <GeneralListSortableTh sortKey="intentionSignOffDate" className="whitespace-nowrap">
                  Intention S/Off
                </GeneralListSortableTh>
                <GeneralListSortableTh sortKey="signOffDate" className="whitespace-nowrap">
                  S/Off Date
                </GeneralListSortableTh>
                <GeneralListSortableTh sortKey="signOffPort" className="whitespace-nowrap">
                  S/Off Port
                </GeneralListSortableTh>
                <GeneralListSortableTh sortKey="signOffReason" className="whitespace-nowrap">
                  S/Off Reason
                </GeneralListSortableTh>
                <th className={cn('sticky top-0 z-10 whitespace-nowrap font-medium', colors.surface, colors.textMuted)}>
                  Repatriation
                </th>
                <GeneralListSortableTh sortKey="earliest" className="whitespace-nowrap">
                  Earliest
                </GeneralListSortableTh>
                <GeneralListSortableTh sortKey="coeDate" className="whitespace-nowrap">
                  COE
                </GeneralListSortableTh>
                <GeneralListSortableTh sortKey="latest" className="whitespace-nowrap">
                  Latest
                </GeneralListSortableTh>
                <GeneralListSortableTh sortKey="extCoeDate" className="whitespace-nowrap">
                  Ext.COE
                </GeneralListSortableTh>
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((row, index) => (
                <tr
                  key={row.transactionId}
                  onClick={() => openCrew(row.crewId)}
                  className={cn(
                    'group cursor-pointer border-b transition last:border-b-0',
                    colors.border,
                    colors.interactiveHover,
                  )}
                >
                  <td onClick={(e) => e.stopPropagation()}>
                    <CrewReportViewAction crewId={row.crewId} from={listReturnPath} />
                  </td>
                  <td className={cn('whitespace-nowrap tabular-nums', colors.textMuted)}>{index + 1}</td>
                  <td className={cn('whitespace-nowrap font-medium', colors.text)}>
                    <Link
                      to={agencyMode ? `/crew/${row.crewId}/file` : `/crew/${row.crewId}`}
                      state={{ from: listReturnPath }}
                      className={cn('hover:underline', colors.primaryFg)}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {row.lastName || ''}
                    </Link>
                  </td>
                  <td className={cn('whitespace-nowrap', colors.text)}>
                    <Link
                      to={agencyMode ? `/crew/${row.crewId}/file` : `/crew/${row.crewId}`}
                      state={{ from: listReturnPath }}
                      className={cn('hover:underline', colors.primaryFg)}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {row.firstName || ''}
                    </Link>
                  </td>
                  <td className={cn('whitespace-nowrap', colors.text)}>{row.nationality || ''}</td>
                  <td className={cn('whitespace-nowrap text-right tabular-nums', colors.text)}>
                    {row.age != null ? row.age : ''}
                  </td>
                  <td className={cn('whitespace-nowrap', colors.text)}>
                    {row.lastRank ? (
                      <Link
                        to={`/crew/${row.crewId}/file`}
                        state={{ from: listReturnPath }}
                        className={cn('hover:underline', colors.primaryFg)}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {row.lastRank}
                      </Link>
                    ) : (
                      ''
                    )}
                  </td>
                  {!agencyMode && (
                    <td className={cn('whitespace-nowrap text-right tabular-nums', colors.text)}>
                      {formatWage(row)}
                    </td>
                  )}
                  <td
                    className={cn(
                      'whitespace-nowrap',
                      row.status === 'ONB' ? 'font-semibold text-emerald-600 dark:text-emerald-400' : colors.text,
                    )}
                  >
                    {row.status || ''}
                  </td>
                  <td className={cn('whitespace-nowrap', colors.textMuted)}>{row.departure || ''}</td>
                  <td className={cn('whitespace-nowrap', colors.textMuted)}>
                    {row.signOnDate ? formatDateGlobal(row.signOnDate) : ''}
                  </td>
                  <td className={cn('whitespace-nowrap text-right tabular-nums', colors.text)}>
                    {row.monthsOnboard != null ? row.monthsOnboard : ''}
                  </td>
                  <td className={cn('whitespace-nowrap', colors.textMuted)}>
                    {row.intentionSignOffDate ? formatDateGlobal(row.intentionSignOffDate) : ''}
                  </td>
                  <td className={cn('whitespace-nowrap', colors.textMuted)}>
                    {row.signOffDate ? formatDateGlobal(row.signOffDate) : ''}
                  </td>
                  <td className={cn('whitespace-nowrap', colors.textMuted)}>{row.signOffPort || ''}</td>
                  <td className={cn('whitespace-nowrap', colors.textMuted)}>{row.signOffReason || ''}</td>
                  <td className={cn('whitespace-nowrap', colors.textMuted)}>{row.repatriation || ''}</td>
                  <td className={cn('whitespace-nowrap', colors.textMuted)}>
                    {row.earliest ? formatDateGlobal(row.earliest) : ''}
                  </td>
                  <td className={cn('whitespace-nowrap', colors.textMuted)}>
                    {row.coeDate ? formatDateGlobal(row.coeDate) : ''}
                  </td>
                  <td className={cn('whitespace-nowrap', colors.textMuted)}>
                    {row.latest ? formatDateGlobal(row.latest) : ''}
                  </td>
                  <td className={cn('whitespace-nowrap', colors.textMuted)}>
                    {row.extCoeDate ? formatDateGlobal(row.extCoeDate) : ''}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className={cn('border-t text-sm font-bold', colors.border, colors.text)}>
                <td
                  colSpan={5}
                  className={cn('sticky bottom-0 z-10 px-3 py-2.5', colors.headerBg, colors.border)}
                >
                  Totals
                </td>
                <td
                  className={cn(
                    'sticky bottom-0 z-10 px-3 py-2.5 text-right tabular-nums whitespace-nowrap',
                    colors.headerBg,
                  )}
                >
                  {tableFooter.avgAge != null
                    ? tableFooter.avgAge.toLocaleString(undefined, { maximumFractionDigits: 2 })
                    : ''}
                </td>
                <td className={cn('sticky bottom-0 z-10', colors.headerBg)} />
                <td
                  className={cn(
                    'sticky bottom-0 z-10 px-3 py-2.5 text-right tabular-nums whitespace-nowrap',
                    colors.headerBg,
                  )}
                >
                  {tableFooter.totalWage != null
                    ? `${tableFooter.totalWage.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}${tableFooter.wageCurrency ? ` ${tableFooter.wageCurrency}` : ''}`
                    : ''}
                </td>
                <td colSpan={3} className={cn('sticky bottom-0 z-10', colors.headerBg)} />
                <td
                  className={cn(
                    'sticky bottom-0 z-10 px-3 py-2.5 text-right tabular-nums whitespace-nowrap',
                    colors.headerBg,
                  )}
                >
                  {tableFooter.totalMonths != null ? Math.round(tableFooter.totalMonths) : ''}
                </td>
                <td colSpan={9} className={cn('sticky bottom-0 z-10', colors.headerBg)} />
              </tr>
            </tfoot>
          </table>
        ) : null}
      </GeneralList>
      {applied ? (
        <ImoCrewListDetailsModal
          open={imoDetailsOpen}
          defaultArrivalDate={applied.periodFrom}
          onClose={() => setImoDetailsOpen(false)}
          onConfirm={(details: ImoCrewListPrintDetails) => {
            setImoDetailsOpen(false);
            navigate(crewListPrintPath('imo', applied, details));
          }}
        />
      ) : null}
    </div>
  );
};

export default CrewListPage;
